import React from "react";
import { Link } from "react-router-dom";
import BasicButton from "../basics/BasicButton";

export type AdminNavGroupId = "coffee" | "cards" | "content" | "store" | "platform";

interface AdminNavLink {
  to: string;
  label: string;
}

interface AdminNavGroup {
  id: AdminNavGroupId;
  title: string;
  links: readonly AdminNavLink[];
}

/**
 * The admin hub used to be one flat wall of ~two dozen identical buttons.
 * Same buttons, same styles, just sorted into four obvious piles so the eye
 * has something to land on. `extras` lets a caller drop a panel (e.g. the
 * respect admin disclosure) into the pile it belongs to.
 */
export const ADMIN_NAV_GROUPS: readonly AdminNavGroup[] = [
  {
    id: "coffee",
    title: "coffee",
    links: [
      { to: "/coffee-admin", label: "Coffee Admin" },
      { to: "/admin/clusters", label: "Coffee Clusters" },
      { to: "/admin/chains", label: "Coffee Chains" },
      { to: "/admin/coffee-masters", label: "Coffee Masters" },
      { to: "/admin/coffee-brands", label: "Coffee Brands" },
    ],
  },
  {
    id: "cards",
    title: "cards & economy",
    links: [
      { to: "/admin/cards", label: "Cards Admin" },
      { to: "/admin/card-engine", label: "Card Engine" },
      { to: "/badges-admin", label: "Badge Admin" },
      { to: "/admin/emojis", label: "Emoji Registry" },
    ],
  },
  {
    id: "content",
    title: "content",
    links: [
      { to: "/admin/contributors", label: "Contributors" },
      { to: "/admin/contributions", label: "Contributions" },
      { to: "/admin/artworks/review", label: "Review queue" },
      { to: "/icons-admin", label: "Icons Admin" },
      { to: "/styleguide", label: "Styles" },
    ],
  },
  {
    id: "store",
    title: "store & prints",
    links: [
      { to: "/admin/marketplace", label: "Store switch" },
      { to: "/admin/print-files", label: "Print files" },
    ],
  },
  {
    id: "platform",
    title: "platform",
    links: [
      { to: "/admin/services", label: "Services" },
      { to: "/admin/notifications", label: "Notifications" },
      { to: "/admin/scheduler", label: "Scheduler" },
      { to: "/admin/events", label: "Event dispatch" },
      { to: "/admin/agent", label: "Sitebot / Agent" },
      { to: "/aotd-irl/admin", label: "AOTD-IRL" },
      { to: "/admin/ai-config", label: "AI Config" },
      { to: "/admin/invites", label: "Invite Stats" },
    ],
  },
] as const;

/** Flat list of every admin destination, in group order. */
export const ADMIN_NAV_LINKS: readonly AdminNavLink[] = ADMIN_NAV_GROUPS.flatMap(
  (group) => group.links,
);

interface AdminNavLinksProps {
  /** Extra content rendered at the end of a group (keyed by group id). */
  extras?: Partial<Record<AdminNavGroupId, React.ReactNode>>;
}

const AdminNavLinks: React.FC<AdminNavLinksProps> = ({ extras }) => (
  <div className="flex flex-col gap-6 w-full max-w-full">
    {ADMIN_NAV_GROUPS.map((group) => (
      <section key={group.id} className="w-full max-w-full">
        <h2 className="font-mono lowercase tracking-wide text-sm font-bold text-ink border-b-2 border-ink pb-1 mb-3 w-full">
          {group.title}
        </h2>
        <div className="flex flex-wrap gap-2 sm:gap-4 justify-center sm:justify-start w-full max-w-full">
          {group.links.map(({ to, label }) => (
            <Link key={to} to={to}>
              <BasicButton text={label} />
            </Link>
          ))}
        </div>
        {extras?.[group.id]}
      </section>
    ))}
  </div>
);

export default AdminNavLinks;
